"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useVoiceToText = useVoiceToText;
const vue_1 = require("vue");
const voiceToTextRequest_1 = require("../../../requests/voiceToTextRequest");
const MAX_RECORDING_DURATION_MS = 60000;
const MAX_AUDIO_SIZE_BYTES = 10 * 1024 * 1024;
function useVoiceToText(options) {
    const isStarting = (0, vue_1.ref)(false);
    const isRecording = (0, vue_1.ref)(false);
    const isTranscribing = (0, vue_1.ref)(false);
    const isActive = (0, vue_1.computed)(() => {
        return isStarting.value || isRecording.value || isTranscribing.value;
    });
    const recordingSeconds = (0, vue_1.ref)(0);
    const errorMessage = (0, vue_1.ref)('');
    let mediaRecorder;
    let microphoneStream;
    let audioChunks = [];
    let audioSize = 0;
    let recordingTimeout;
    let recordingStartedAt = 0;
    let discardRecording = false;
    let recordingFailed = false;
    function clearTimers() {
        if (recordingTimeout !== undefined) {
            clearTimeout(recordingTimeout);
            recordingTimeout = undefined;
        }
    }
    function releaseMicrophone() {
        microphoneStream?.getTracks().forEach(track => track.stop());
        microphoneStream = undefined;
    }
    function stopRecording() {
        if (mediaRecorder?.state === 'recording') {
            mediaRecorder.stop();
        }
    }
    async function transcribe(audio) {
        if (audio.size === 0) {
            errorMessage.value = '音声を録音できませんでした。';
            return;
        }
        if (audio.size > MAX_AUDIO_SIZE_BYTES) {
            errorMessage.value = '録音データが10MBを超えたため送信できませんでした。';
            return;
        }
        isTranscribing.value = true;
        errorMessage.value = '';
        try {
            const text = await (0, voiceToTextRequest_1.voiceToTextRequest)(options.userId, options.language, audio);
            if (!text.trim()) {
                errorMessage.value = '';
                return;
            }
            options.onTranscript(text);
        }
        catch {
            errorMessage.value = '音声の文字変換に失敗しました。もう一度お試しください。';
        }
        finally {
            isTranscribing.value = false;
        }
    }
    async function startRecording() {
        if (isActive.value) {
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            errorMessage.value = 'このブラウザーは音声入力に対応していません。';
            return;
        }
        isStarting.value = true;
        errorMessage.value = '';
        discardRecording = false;
        recordingFailed = false;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (discardRecording) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            microphoneStream = stream;
            const preferredMime = 'audio/webm;codecs=opus';
            const options = MediaRecorder.isTypeSupported(preferredMime)
                ? { mimeType: preferredMime }
                : undefined;
            mediaRecorder = new MediaRecorder(stream, options);
            audioChunks = [];
            audioSize = 0;
            recordingSeconds.value = 0;
            mediaRecorder.ondataavailable = event => {
                recordingSeconds.value = Math.floor((Date.now() - recordingStartedAt) / 1000);
                if (!event.data.size) {
                    return;
                }
                audioChunks.push(event.data);
                audioSize += event.data.size;
                if (audioSize > MAX_AUDIO_SIZE_BYTES) {
                    stopRecording();
                }
            };
            mediaRecorder.onerror = () => {
                recordingFailed = true;
                errorMessage.value = '録音中にエラーが発生しました。';
                stopRecording();
            };
            mediaRecorder.onstop = () => {
                clearTimers();
                releaseMicrophone();
                isRecording.value = false;
                const audio = new Blob(audioChunks, { type: mediaRecorder?.mimeType || 'audio/webm' });
                audioChunks = [];
                mediaRecorder = undefined;
                if (!discardRecording && !recordingFailed) {
                    void transcribe(audio);
                }
            };
            recordingStartedAt = Date.now();
            mediaRecorder.start(1000);
            isRecording.value = true;
            errorMessage.value = '';
            recordingTimeout = setTimeout(stopRecording, MAX_RECORDING_DURATION_MS);
        }
        catch {
            releaseMicrophone();
            mediaRecorder = undefined;
            errorMessage.value = 'マイクを使用できません。ブラウザーの権限を確認してください。';
        }
        finally {
            isStarting.value = false;
        }
    }
    function toggleRecording() {
        if (isRecording.value) {
            stopRecording();
            return;
        }
        void startRecording();
    }
    (0, vue_1.onBeforeUnmount)(() => {
        discardRecording = true;
        clearTimers();
        stopRecording();
        releaseMicrophone();
    });
    return {
        isRecording,
        isActive,
        recordingSeconds,
        errorMessage,
        toggleRecording,
    };
}
